import { AdjustmentImpactEnum, AdjustmentKindEnum } from '@koob/enums';
import {
  getAdjustmentScopeLabel,
  isEntireScopeAdjustment,
} from './adjustmentScope';
import { KoobBadge, KoobButton, icons } from '@koob/ui';
import { useTranslation } from 'react-i18next';
import { formatDateTime, DATE_TIME } from '@koob/dates';
import { formatCurrency } from '../utils';

const IMPACT_BADGES = {
  [AdjustmentImpactEnum.MARGIN]: { color: 'purple', label: 'Margin' },
  [AdjustmentImpactEnum.PURCHASE_PRICE]: {
    color: 'blue',
    label: 'Purchase price',
  },
  [AdjustmentImpactEnum.SELLING_PRICE]: {
    color: 'gray',
    label: 'Selling price',
  },
};

const deltaColor = (amount) =>
  amount < 0 ? 'text-rose-600' : 'text-emerald-600';

const isMissing = (amount) => amount === null || amount === undefined;

// Adjustments created before the impact split have no stored base, so the
// recalculated value is unknown: only their delta can be shown.
const AmountChange = ({
  base,
  adjusted,
  delta,
  currency,
  adjustedClassName = 'text-slate-700',
}) => {
  if (isMissing(base) || isMissing(adjusted)) {
    return (
      <div className={`font-medium ${deltaColor(delta)}`}>
        {formatCurrency({ amount: delta, currency })}
      </div>
    );
  }

  if (base === adjusted) {
    return (
      <div className={`font-semibold ${adjustedClassName}`}>
        {formatCurrency({ amount: adjusted, currency })}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-baseline gap-1.5">
      <span className="line-through text-slate-400">
        {formatCurrency({ amount: base, currency })}
      </span>
      <span className={`font-semibold ${adjustedClassName}`}>
        {formatCurrency({ amount: adjusted, currency })}
      </span>
    </div>
  );
};

const SummaryCell = ({ label, children }) => (
  <div>
    <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">
      {label}
    </p>
    {children}
  </div>
);

const AdjustmentSummary = ({ pricingSummary, currency, t }) => {
  if (!pricingSummary) return null;

  const { baseTotal, adjustmentAmount, totalPrice } = pricingSummary;
  const isNegative = adjustmentAmount < 0;

  return (
    <div className="rounded-2xl border border-solid border-k-gray bg-white p-5">
      <p className="text-sm font-semibold text-slate-700">
        {t('summaryTitle')}
      </p>
      <p className="text-xs text-slate-400 mb-4">{t('summarySubtitle')}</p>
      <div className="grid grid-cols-3 gap-4 text-sm">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">
            {t('basePrice')}
          </p>
          <p className="font-semibold text-slate-700">
            {formatCurrency({ amount: baseTotal, currency })}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">
            {t('summaryAdjustment')}
          </p>
          <p
            className={`font-semibold ${isNegative ? 'text-rose-600' : 'text-emerald-600'}`}
          >
            {formatCurrency({ amount: adjustmentAmount, currency })}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">
            {t('summaryTotalPrice')}
          </p>
          <p className="font-semibold text-orange-500">
            {formatCurrency({ amount: totalPrice, currency })}
          </p>
        </div>
      </div>
    </div>
  );
};

// DMC-only summary: the purchase and margin figures are stripped from the
// API responses served to a TO, so it only renders when explicitly requested.
const FinancialSummary = ({
  pricingSummary,
  financialSummary,
  currency,
  t,
}) => {
  const { purchaseSummary, marginSummary } = financialSummary;

  return (
    <div className="rounded-2xl border border-solid border-k-gray bg-white p-5">
      <p className="text-sm font-semibold text-slate-700">
        {t('summaryTitle')}
      </p>
      <p className="text-xs text-slate-400 mb-4">{t('summarySubtitle')}</p>
      <div className="grid grid-cols-3 gap-4 text-sm">
        <SummaryCell label={t('summaryTotalAdjustments')}>
          <p
            className={`font-semibold ${deltaColor(pricingSummary?.adjustmentAmount)}`}
          >
            {formatCurrency({
              amount: pricingSummary?.adjustmentAmount,
              currency,
            })}
          </p>
        </SummaryCell>
        <SummaryCell label={t('summaryImpactPurchase')}>
          <p className={`font-semibold ${deltaColor(purchaseSummary?.delta)}`}>
            {formatCurrency({ amount: purchaseSummary?.delta, currency })}
          </p>
        </SummaryCell>
        <SummaryCell label={t('summaryImpactMargin')}>
          <p className={`font-semibold ${deltaColor(marginSummary?.delta)}`}>
            {formatCurrency({ amount: marginSummary?.delta, currency })}
          </p>
        </SummaryCell>
      </div>
      <div className="grid grid-cols-3 gap-4 text-sm mt-4 pt-4 border-t border-slate-100">
        <SummaryCell label={t('summaryPurchasePrice')}>
          <AmountChange
            base={purchaseSummary?.basePurchasePrice}
            adjusted={purchaseSummary?.adjustedPurchasePrice}
            delta={purchaseSummary?.delta}
            currency={currency}
          />
        </SummaryCell>
        <SummaryCell label={t('summaryMargin')}>
          <AmountChange
            base={marginSummary?.baseMargin}
            adjusted={marginSummary?.adjustedMargin}
            delta={marginSummary?.delta}
            currency={currency}
          />
        </SummaryCell>
        <SummaryCell label={t('summaryNewTotal')}>
          <AmountChange
            base={pricingSummary?.baseTotal}
            adjusted={pricingSummary?.totalPrice}
            delta={pricingSummary?.adjustmentAmount}
            currency={currency}
            adjustedClassName="text-orange-500"
          />
        </SummaryCell>
      </div>
    </div>
  );
};

const AdjustmentList = ({
  adjustments,
  currency,
  pricingSummary,
  translationKey,
  financialSummary,
  showFinancials = false,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation(translationKey);

  if (adjustments.length === 0) return null;

  const sorted = [...adjustments].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
  );

  return (
    <div className="space-y-4">
      {showFinancials && financialSummary ? (
        <FinancialSummary
          pricingSummary={pricingSummary}
          financialSummary={financialSummary}
          currency={currency}
          t={t}
        />
      ) : (
        <AdjustmentSummary
          pricingSummary={pricingSummary}
          currency={currency}
          t={t}
        />
      )}
      {sorted.map((adjustment) => {
        const isDiscount = adjustment.type === AdjustmentKindEnum.DISCOUNT;
        // An API that predates the impact split sends no impact: without it
        // the card has neither financial details nor edit/delete routes.
        const hasImpact = Boolean(adjustment.impact);
        const hasFinancials = showFinancials && hasImpact;
        const impactBadge = hasFinancials && IMPACT_BADGES[adjustment.impact];
        const canEdit = hasImpact && Boolean(onEdit);
        const canDelete = hasImpact && Boolean(onDelete);
        // The impact tells the scope apart, never the target: a "Specific
        // products" adjustment targets the booking that carries it, so its
        // target type would read as an entire booking.
        const scopeLabel = getAdjustmentScopeLabel(adjustment, t);
        const isEntireScope = isEntireScopeAdjustment(adjustment);

        return (
          <div
            key={adjustment.id}
            className="rounded-2xl border border-solid border-k-gray bg-white"
          >
            {/* HEADER */}
            <div className="p-4 border-b border-slate-100">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <KoobBadge color={isDiscount ? 'red' : 'green'}>
                        {t(`kind.${adjustment.type}`, adjustment.type)}
                      </KoobBadge>

                      {impactBadge && (
                        <KoobBadge color={impactBadge.color}>
                          {t(`impact.${adjustment.impact}`, impactBadge.label)}
                        </KoobBadge>
                      )}

                      {adjustment.label && (
                        <p className="text-sm text-slate-500 mt-1">
                          {adjustment.label}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="text-xs text-slate-400 whitespace-nowrap text-right">
                  {adjustment.appliedByName && (
                    <div className="font-medium text-slate-500">
                      {adjustment.appliedByName}
                    </div>
                  )}
                  {formatDateTime(adjustment.createdAt, DATE_TIME)}
                </div>

                {(canEdit || canDelete) && (
                  <div className="flex items-center gap-1">
                    {canEdit && (
                      <KoobButton
                        size="sm"
                        variant="ghost"
                        onClick={() => onEdit(adjustment)}
                      >
                        <i className={icons.edit} />
                        {t('list.edit')}
                      </KoobButton>
                    )}
                    {canDelete && (
                      <KoobButton
                        size="sm"
                        variant="ghost"
                        onClick={() => onDelete(adjustment)}
                      >
                        <i className={icons.delete} />
                        {t('list.delete')}
                      </KoobButton>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* ITEMS */}
            <div className="divide-y divide-slate-100">
              {hasFinancials ? (
                <div className="p-4 flex flex-col gap-2">
                  {scopeLabel && (
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                      {scopeLabel}
                    </p>
                  )}
                  <div
                    className={`grid gap-4 text-sm ${
                      isEntireScope ? 'grid-cols-3' : 'grid-cols-4'
                    }`}
                  >
                    {!isEntireScope && (
                      <div>
                        <div className="text-slate-400 text-xs">
                          {t('summaryPurchasePrice')}
                        </div>
                        <AmountChange
                          base={adjustment.basePurchasePrice}
                          adjusted={adjustment.adjustedPurchasePrice}
                          delta={adjustment.purchasePriceDelta}
                          currency={currency}
                        />
                      </div>
                    )}

                    {/* Every scope moves the margin; only a specific product
                        adjustment can also move the purchase price. */}
                    <div>
                      <div className="text-slate-400 text-xs">
                        {t('summaryMargin')}
                      </div>
                      <AmountChange
                        base={adjustment.baseMargin}
                        adjusted={adjustment.adjustedMargin}
                        delta={adjustment.marginDelta}
                        currency={currency}
                      />
                    </div>

                    <div>
                      <div className="text-slate-400 text-xs">
                        {t('list.adjustmentDelta')}
                      </div>
                      <div
                        className={`font-medium ${deltaColor(adjustment.adjustmentAmount)}`}
                      >
                        {formatCurrency({
                          amount: adjustment.adjustmentAmount,
                          currency: currency,
                        })}
                      </div>
                    </div>

                    <div>
                      <div className="text-slate-400 text-xs">
                        {t('summaryTotalPrice')}
                      </div>
                      <AmountChange
                        base={adjustment.basePrice}
                        adjusted={adjustment.totalPrice}
                        delta={adjustment.adjustmentAmount}
                        currency={currency}
                        adjustedClassName="text-orange-500"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div key={adjustment.id} className="p-4 flex flex-col gap-2">
                  {(adjustment.name || adjustment.productName) && (
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                      {adjustment.name ?? adjustment.productName}
                    </p>
                  )}
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-slate-400 text-xs">
                        {t('basePrice')}
                      </div>
                      <div className="font-medium text-slate-700">
                        {formatCurrency({
                          amount: adjustment.basePrice,
                          currency: currency,
                        })}
                      </div>
                    </div>

                    <div>
                      <div className="text-slate-400 text-xs">{t('title')}</div>
                      <div
                        className={`font-medium ${
                          adjustment.adjustmentAmount < 0
                            ? 'text-rose-600'
                            : 'text-emerald-600'
                        }`}
                      >
                        {formatCurrency({
                          amount: adjustment.adjustmentAmount,
                          currency: currency,
                        })}
                      </div>
                    </div>

                    <div>
                      <div className="text-slate-400 text-xs">{t('total')}</div>
                      <div className="font-semibold text-orange-500">
                        {formatCurrency({
                          amount: adjustment.totalPrice,
                          currency: currency,
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AdjustmentList;
